import React, { useEffect, useRef } from 'react';
import { SidebarBrand } from './SidebarBrand';
import { AddAudienceButton } from './AddAudienceButton';
import { SidebarSearch } from './SidebarSearch';
import { SidebarNav } from './SidebarNav';
import { RecentSegments } from './RecentSegments';
import { SidebarCollapseToggle } from './SidebarCollapseToggle';
import { SidebarFooter } from './SidebarFooter';
import { RAIL_WIDTH_PX, SIDEBAR_FOOTER_HEIGHT_PX, SIDEBAR_WIDTH_PX } from './constants';
import type { NavItem } from './navItems';

/** Clearance kept around the active item when it is scrolled into view. */
const SCROLL_MARGIN_PX = 8;

export interface ShellSidebarProps {
  readonly items: readonly NavItem[];
  readonly pathname: string;
  readonly collapsed: boolean;
  readonly onToggleCollapsed: () => void;
  readonly getNavTo: (path: string) => string;
  /** False when the width forces the rail: the pin toggle would do nothing. */
  readonly canToggle?: boolean;
  /**
   * Inside the phone drawer (AL-1972): the drawer places and sizes it, so it
   * drops the fixed positioning and its own width.
   */
  readonly inDrawer?: boolean;
}

/**
 * The app's sidebar, in its two widths — at every window width (AL-1911).
 *
 * One component rather than two: the rail is the same furniture with the
 * labels taken away, and keeping it here is what stops the two drifting apart
 * as items are added.
 */
export const ShellSidebar: React.FC<ShellSidebarProps> = ({
  items,
  pathname,
  collapsed,
  onToggleCollapsed,
  getNavTo,
  canToggle = true,
  inDrawer = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // On a short screen the page the user lands on can sit below the fold (a
  // deep link, the command palette, a recent segment). Bring its item into
  // view inside the sidebar only, so the page itself never jumps. `items` is
  // in the deps because the gated items arrive with the profile, after mount.
  useEffect(() => {
    const scroller = scrollRef.current;
    const active = scroller?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!scroller || !active) return;
    const box = scroller.getBoundingClientRect();
    const item = active.getBoundingClientRect();
    if (item.top < box.top) scroller.scrollTop -= box.top - item.top + SCROLL_MARGIN_PX;
    else if (item.bottom > box.bottom)
      scroller.scrollTop += item.bottom - box.bottom + SCROLL_MARGIN_PX;
  }, [pathname, collapsed, items]);

  return (
    <aside
      data-testid="shell-sidebar"
      data-collapsed={collapsed}
      style={inDrawer ? undefined : { width: collapsed ? RAIL_WIDTH_PX : SIDEBAR_WIDTH_PX }}
      className={
        inDrawer
          ? 'flex h-full flex-col bg-v11-sidebar-bg p-4'
          : 'fixed bottom-0 left-0 top-0 z-30 flex flex-col border-r border-v11-border bg-v11-sidebar-bg p-4 transition-[width] duration-200 ease-in-out'
      }
    >
      {/* Everything above the footer scrolls as one block on a short screen
        (AL-1918): the sidebar is fixed to the viewport, so without this the
        nav, the recent segments and the footer were cut off with no way to
        reach them. The negative margins put the scrollbar on the sidebar's
        edge and the top edge on the sidebar's, with the padding giving the
        focus rings room so the overflow does not cut them. At normal heights
        nothing overflows and the layout is unchanged. */}
      <div
        ref={scrollRef}
        data-testid="shell-sidebar-scroll"
        className="v11-scrollbar -mx-4 -mt-4 flex min-h-0 flex-1 scroll-pb-1 scroll-pt-4 flex-col overflow-y-auto overflow-x-hidden px-4 pb-1 pt-4"
      >
        <div
          className={`flex shrink-0 flex-col ${collapsed ? 'items-center gap-[7px]' : 'gap-[10px]'}`}
        >
          <SidebarBrand collapsed={collapsed} />
          <AddAudienceButton collapsed={collapsed} />
          <SidebarSearch collapsed={collapsed} items={items} pathname={pathname} />
        </div>

        <SidebarNav items={items} pathname={pathname} collapsed={collapsed} getNavTo={getNavTo} />

        {/* Takes the slack above the footer. A block, not a flex column: the
          list (four segments at most) keeps its full height and the block
          above scrolls instead, rather than squeezing it out of sight. */}
        <div className="flex-1">{!collapsed && <RecentSegments />}</div>
      </div>

      <div className={`flex shrink-0 flex-col ${collapsed ? 'items-center' : ''}`}>
        {canToggle && <SidebarCollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />}
        {/* A fixed height, bottom-aligned, in both widths: the rail stacks its
          icons and is taller, so a footer sized by its content pushed the
          toggle 40px up on closing (AL-1943). */}
        <div
          data-testid="shell-sidebar-footer"
          style={{ height: SIDEBAR_FOOTER_HEIGHT_PX }}
          className={`flex flex-col justify-end ${collapsed ? 'items-center' : ''}`}
        >
          <SidebarFooter collapsed={collapsed} />
        </div>
      </div>
    </aside>
  );
};
